import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import { apiUrl } from "./auth";

/** Which cached queries a `changed` event refreshes (mirrors the API's @Emits entities). */
const KEYS: Record<string, string[]> = {
  hospitals: ["hospitals"],
  users: ["users", "me"],
  ambulances: ["ambulances"],
  "unit-codes": ["unit-codes"],
  units: ["units-active"],
  checklists: ["checklists"],
  "bag-checklists": ["bag-checklists"],
  "api-clients": ["api-clients"],
  webhooks: ["webhooks"],
};

/**
 * Live data: one WebSocket per signed-in browser. The server broadcasts
 * `changed` {entity} after every mutation in the tenant and we refetch the
 * affected lists, so every device shows new submissions within a second.
 * After a reconnect (e.g. back from offline) everything is refetched once.
 */
export function useRealtime() {
  const auth = useAuth();
  const qc = useQueryClient();
  const token = auth.user?.access_token;

  useEffect(() => {
    if (!token) return;
    const socket = io(apiUrl, { transports: ["websocket"], auth: { token }, reconnectionDelayMax: 10_000 });
    let connectedBefore = false;
    socket.on("connect", () => {
      if (connectedBefore) void qc.invalidateQueries();
      connectedBefore = true;
    });
    socket.on("changed", (ev: { entity: string }) => {
      for (const key of KEYS[ev.entity] ?? []) void qc.invalidateQueries({ queryKey: [key] });
    });
    return () => {
      socket.disconnect();
    };
  }, [token, qc]);
}
